import { createRootRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';

import { useSession } from '@platform/api-client/react';
import { AppShell, ToastProvider, useDocumentTitle, VersionBadge } from '@platform/ui';

import { AccountMenu } from '../auth/account-menu';
import { AuthLayout } from '../auth/components';
import { navigation, pageTitle } from '../lib/nav';
import { SessionExpiryWatcher } from '../lib/session/expiry';
import { MFA_ENROLMENT_PATHS, RequireAuth, RequireMfa } from '../lib/session/guards';
import { ImpersonationBannerConnected } from '../lib/session/impersonation';
import { usePermissions } from '../lib/session/permissions';
import { StepUpProvider } from '../lib/session/step-up';
import { useClinicBranding } from '../lib/tenant-branding';
import { APP_BUILD, useApiVersion } from '../version';

export const Route = createRootRoute({
  component: RootLayout,
});

function Shell() {
  const api = useApiVersion();
  /* M17.3 (FR-UI-03): the clinic's own colours, logo and favicon on its admin app. */
  useClinicBranding();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { can } = usePermissions();

  return (
    <AppShell
      /* Filtered by the caller's permissions from `GET /auth/session`. Display only:
         every endpoint behind every item checks the same key (rules §17.2, §22.2). */
      nav={navigation(pathname, can)}
      tenantName={<TenantName />}
      logoVariant="tenant"
      /* Always above the top bar, on every screen, for the whole support session
         (rules §20.5, ADR-005). It draws itself from the session, so it appears and
         disappears with the server's own record and never with local state. */
      banner={<ImpersonationBannerConnected />}
      renderLink={(item, props) => (
        <Link
          to={item.href ?? '/'}
          className={props.className}
          aria-current={props['aria-current']}
        >
          {props.children}
        </Link>
      )}
      userMenu={
        /* TODO(M2.11): the feedback entry point sits beside this. */
        <AccountMenu
          renderLink={({ to, className, children, onClick }) => (
            <Link to={to} className={className} onClick={onClick}>
              {children}
            </Link>
          )}
        />
      }
      footer={<VersionBadge app={APP_BUILD} api={api} />}
    >
      <SessionExpiryWatcher />
      <RequireAuth>
        <RequireMfa>
          <Outlet />
        </RequireMfa>
      </RequireAuth>
    </AppShell>
  );
}

/**
 * The clinic's own name in the top bar, from `session.tenant` - which the server
 * resolved from the host, never from anything the page chose (rules §18.4). Before the
 * session has loaded, and for a session with no tenant, it falls back to the product
 * name rather than to an empty bar.
 */
function TenantName() {
  const session = useSession();
  return <>{session.data?.tenant?.name ?? 'HeyAllergy · Clinic Admin'}</>;
}

/**
 * The app's outermost frame (M2.7).
 *
 * Two layouts, chosen by route rather than by state:
 *
 * - **`/login` and `/mfa/enroll`** render bare, inside `AuthLayout`. A left nav around a
 *   sign-in form is a menu of links the person cannot follow, and a shell implies they
 *   are already inside the app.
 * - **Everything else** renders the `AppShell`, behind `RequireAuth` and `RequireMfa`.
 *   The guards sit *inside* the shell, so the nav, the banner and the account menu stay
 *   on screen while the session is being checked - a staff member who refreshes
 *   mid-session sees the app load rather than a flash of an empty page.
 *
 * `StepUpProvider` wraps both: the dialog it renders must outlive any one screen, and a
 * step-up refusal can arrive from a call started on a route that has since changed.
 *
 * **`notifyUnguarded={false}`** is the one deliberate difference from the super admin
 * console. `features/integrations/step-up-prompt.tsx` (M3.9, itself a `TODO(M2.7)`)
 * still catches `403 MFA_STEP_UP_REQUIRED` where the call was made and runs its own
 * retry; letting the client callback open this dialog too would put two modals on screen
 * for one refusal. Every seam that uses `useStepUp().guard` is unaffected, because the
 * guarded path never depended on the callback. Flip this to `true` - or delete the prop -
 * once that seam adopts `guard` (`Docs/ADMIN_AUTH_UI.md` §6).
 */
function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const bare = MFA_ENROLMENT_PATHS.includes(pathname);

  /* M13.7 (WCAG 2.4.2). One `<title>` in index.html means every route, every tab and
     every history entry read "HeyAllergy Clinic Admin"; this names the screen. */
  useDocumentTitle(pageTitle(pathname));

  return (
    <ToastProvider>
      <StepUpProvider notifyUnguarded={false}>
        {bare ? (
          <AuthLayout>
            <Outlet />
          </AuthLayout>
        ) : (
          <Shell />
        )}
      </StepUpProvider>
    </ToastProvider>
  );
}
